import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ChatMode = 'add' | 'ask';

interface ChatTransferState {
  initialMessage: string | null;
  mode: ChatMode;
  imageData?: string | null; // Base64 image data for file uploads
  startRecording?: boolean; // Flag to start voice recording
}

interface ChatTransferContextType {
  state: ChatTransferState;
  setTransfer: (message: string, mode: ChatMode) => void;
  setTransferWithImage: (imageData: string, mode: ChatMode) => void;
  setTransferWithRecording: (mode: ChatMode) => void;
  consumeTransfer: () => ChatTransferState | null;
  clearTransfer: () => void;
}

const ChatTransferContext = createContext<ChatTransferContextType | undefined>(undefined);

export const ChatTransferProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ChatTransferState>({
    initialMessage: null,
    mode: 'add',
    imageData: null,
    startRecording: false,
  });

  const setTransfer = useCallback((message: string, mode: ChatMode) => {
    setState({
      initialMessage: message,
      mode,
      imageData: null,
      startRecording: false,
    });
  }, []);

  const setTransferWithImage = useCallback((imageData: string, mode: ChatMode) => {
    setState({
      initialMessage: null,
      mode,
      imageData,
      startRecording: false,
    });
  }, []);

  const setTransferWithRecording = useCallback((mode: ChatMode) => {
    setState({
      initialMessage: null,
      mode,
      imageData: null,
      startRecording: true,
    });
  }, []);

  const consumeTransfer = useCallback(() => {
    if (state.initialMessage || state.imageData || state.startRecording) {
      const currentState = { ...state };
      setState({
        initialMessage: null,
        mode: 'add',
        imageData: null,
        startRecording: false,
      });
      return currentState;
    }
    return null;
  }, [state]);

  const clearTransfer = useCallback(() => {
    setState({
      initialMessage: null,
      mode: 'add',
      imageData: null,
      startRecording: false,
    });
  }, []);

  return (
    <ChatTransferContext.Provider value={{ 
      state, 
      setTransfer, 
      setTransferWithImage,
      setTransferWithRecording,
      consumeTransfer, 
      clearTransfer 
    }}>
      {children}
    </ChatTransferContext.Provider>
  );
};

export const useChatTransfer = () => {
  const context = useContext(ChatTransferContext);
  if (context === undefined) {
    throw new Error('useChatTransfer must be used within a ChatTransferProvider');
  }
  return context;
};
